import React, { useEffect, useState, useMemo } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { HashLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import ReactPaginate from "react-paginate";

import Sidebar from "../../common/Sidebar";
import { authSelector } from "../../apps/auth";
import {
  deviceSelector,
  changeLimit,
  changePage,
  fetchDevices,
} from "../../apps/device";
import { pageOptions } from "../../utils/constants";

const DeviceList = () => {
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);
  const { devices, total, limit, page } = useSelector(deviceSelector);

  const [searchTerm, setSearchTerm] = useState("");
  const [os, setOs] = useState("");
  const [isOnline, setIsOnline] = useState("");
  const [loading, setLoading] = useState(false);
  const [purchaseType, setPurchaseType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const pageCount = useMemo(() => {
    return Math.ceil(total / limit);
  }, [total, limit]);

  const configObject = {
    headers: {
      Authorization: "Bearer " + token,
    },
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await dispatch(changePage(1)); // ✅ reset page

      await dispatch(
        fetchDevices({
          ...configObject,
          params: {
            limit,
            page: 1, // ✅ force page 1
            search: searchTerm,
            os,
            isOnline,
            purchaseType,
            startDate: startDate ? `${startDate}T00:00:00.000Z` : "",
            endDate: endDate ? `${endDate}T23:59:59.999Z` : "",
          },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const limitHandler = async (e) => {
    const newLimit = e.target.value;
    await dispatch(changeLimit(newLimit));
    await dispatch(
      fetchDevices({
        ...configObject,
        params: {
          limit: newLimit,
          page: 1,
          search: searchTerm,
          os,
          isOnline,
          purchaseType,
          startDate: startDate ? `${startDate}T00:00:00.000Z` : "",
          endDate: endDate ? `${endDate}T23:59:59.999Z` : "",
        },
      })
    );
  };

  const handlePageClick = async (data) => {
    const currentPage = data.selected + 1;
    await dispatch(changePage(currentPage));
    await dispatch(
      fetchDevices({
        ...configObject,
        params: {
          limit,
          page: currentPage,
          search: searchTerm,
          os,
          isOnline,
          purchaseType,
          startDate: startDate ? `${startDate}T00:00:00.000Z` : "",
          endDate: endDate ? `${endDate}T23:59:59.999Z` : "",
        },
      })
    );
  };

  return (
    <>
      <Sidebar />

      {loading ? (
        <div className="spinner">
          <HashLoader color="#757575" size={75} />
        </div>
      ) : (
        <div className="card shadow-sm rounded">
          {/* Header */}
          <div className="card-header bg-white">
            <div
              className="
    d-flex
    flex-wrap
    flex-lg-nowrap
    align-items-end
    gap-2
    ms-lg-auto
  "
              style={{ minWidth: 0 }}
            >
              {/* Heading */}
              <h3 className="mb-0 flex-shrink-0">Devices</h3>

              {/* Filters */}
              <div className="d-flex flex-wrap flex-lg-nowrap align-items-end gap-2 ms-lg-auto w-100">
                {/* OS */}
                <select
                  className="form-select form-select-sm w-auto"
                  value={os}
                  onChange={(e) => setOs(e.target.value)}
                >
                  <option value="">All OS</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                  <option value="aosp">Android TV</option>
                  <option value="tvos">Apple TV</option>
                </select>

                {/* Status */}
                <select
                  className="form-select form-select-sm w-auto"
                  value={isOnline}
                  onChange={(e) => setIsOnline(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="true">Online</option>
                  <option value="false">Offline</option>
                </select>

                {/* Purchase Type */}
                <select
                  className="form-select form-select-sm w-auto"
                  value={purchaseType}
                  onChange={(e) => setPurchaseType(e.target.value)}
                >
                  <option value="">All Subscriptions</option>
                  <option value="purchases">All Purchases</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="lifetime">Lifetime</option>
                  <option value="none">No Purchase</option>
                </select>

                {/* Start Date */}
                <input
                  type="date"
                  className="form-control form-control-sm w-auto"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />

                {/* End Date */}
                <input
                  type="date"
                  className="form-control form-control-sm w-auto"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />

                {/* Search + button */}
                <div
                  className="d-flex gap-1 flex-shrink-1"
                  style={{ minWidth: 0 }}
                >
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        fetchData();
                      }
                    }}
                    style={{ width: 160 }}
                  />
                  <Button
                    className="btn btn-primary btn-sm"
                    onClick={fetchData}
                  >
                    Search
                  </Button>
                </div>

                {/* Clear */}
                <Button
                  variant="outline-secondary"
                  className="btn-sm"
                  onClick={async () => {
                    setSearchTerm("");
                    setOs("");
                    setIsOnline("");
                    setPurchaseType("");
                    setStartDate("");
                    setEndDate("");

                    await dispatch(changePage(1));

                    await dispatch(
                      fetchDevices({
                        headers: {
                          Authorization: "Bearer " + token,
                        },
                        params: {
                          limit,
                          page: 1,
                          search: "",
                          os: "",
                          isOnline: "",
                          purchaseType: "",
                          startDate: "",
                          endDate: "",
                        },
                      })
                    );
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle mb-0"
            >
              <thead>
                <tr>
                  <th>UUID</th>
                  <th>Device Name</th>
                  <th>Model</th>
                  <th>OS</th>
                  <th>Purchase Type</th>
                  <th>App Version</th>
                  <th>IP</th>
                  <th>Online</th>
                  <th>Joined At</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {total > 0 ? (
                  devices.map((item) => (
                    <tr key={item._id}>
                      <td>{item.uuid}</td>
                      <td>{item.device_name}</td>
                      <td>{item.device_model}</td>
                      <td>{item.os_version}</td>

                      {/* Purchase Type */}
                      <td>
                        {item.purchaseType ? (
                          <span className="badge bg-info text-dark">
                            {item.purchaseType}
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            No Purchase
                          </span>
                        )}
                      </td>

                      {/* App Version */}
                      <td>{item.app_version || "-"}</td>

                      {/* IP */}
                      <td>{item.ip || "-"}</td>

                      {/* Online */}
                      <td>
                        <span
                          className={`badge ${
                            item.isOnline ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {item.isOnline ? "Online" : "Offline"}
                        </span>
                      </td>

                      {/* Joined At */}
                      <td>
                        {item.joinedAt
                          ? new Date(item.joinedAt).toLocaleDateString()
                          : "-"}
                      </td>

                      {/* Created At */}
                      <td>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString()
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No record found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 small">Show:</label>
                <select
                  className="form-select form-select-sm w-auto"
                  onChange={limitHandler}
                  value={limit}
                >
                  {pageOptions.map((p) => (
                    <option value={p.value} key={p.label}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <ReactPaginate
                previousLabel={"‹"}
                nextLabel={"›"}
                breakLabel={"..."}
                pageCount={pageCount || 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageClick}
                containerClassName={"pagination mb-0"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                breakClassName={"page-item"}
                breakLinkClassName={"page-link"}
                activeClassName={"active"}
                forcePage={page - 1}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DeviceList;
